import {
  Image,
  Picture,
  Video,
  Box,
  Divider,
  Flex,
  Typography,
  MediaBox,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"

import Slider from "dependencies/carousel"
import { jewlr_t } from "helpers/application"

import { Paragraph, SectionTitle } from "./components"

const DiamondImageSlide = styled.div`
  margin: 0 auto;
  position: relative;
  width: 80%;
  ${(props) => props.theme.mediaQueries.tablet`
    width: 40%;
  `}
  :after,
  :before {
    display: block;
    content: " ";
    position: absolute;
    top: 9px;
    height: 5px;
    width: 5px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.black};
    z-index: -1;
  }
  :before {
    left: 0;
  }
  :after {
    right: 0;
  }
`

const DiamondInputSlider = styled.input`
  -webkit-appearance: none;
  background: transparent;
  width: 100%;
  :focus {
    outline: none;
  }
  ::-webkit-slider-thumb {
    -webkit-appearance: none;
    margin-top: -7px;
    border: none;
    height: 14px;
    width: 14px;
    border-radius: 50%;
    background: ${(props) => props.theme.colors.primary};
    cursor: pointer;
  }
  ::-webkit-slider-runnable-track {
    background: ${(props) => props.theme.colors.black};
    border: none;
    border-radius: 3px;
    height: 1px;
  }
`

const LabelLeft = styled.span`
  float: left !important;
  margin-top: 5px !important;
`

const LabelRight = styled.span`
  float: right !important;
  margin-top: 5px !important;
`

const ShopButton = styled(Link)`
  background-color: ${(props) => props.theme.colors.black};
  border: 1px solid ${(props) => props.theme.colors.black};
  border-radius: 0;
  color: white;
  display: inline-block;
  letter-spacing: 1px;
  min-width: 150px;
  outline: none;
  padding: 5px;
  padding: 0.5em 0.8em;
  &:focus {
    outline: none;
  }
  &:hover {
    color: white;
  }
`

const FloatImage = styled.div`
  float: left;
  ${(props) => props.theme.mediaQueries.tablet`
    flex:1 1 100%;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    flex:1 1 50%;
  `}
`

const ArrowImg = styled(Image)`
  margin: 0 auto;
  position: relative;
  top: 50%;
`
const SubTitle = styled(Typography).attrs({
  as: "strong",
})`
  font-size: 16px;
`
const CutSubTitle = styled(Typography).attrs({
  fontSize: 16,
  fontWeight: "normal",
  letterSpacing: { tablet: "1px" },
  mb: 1,
})``

const Title = styled(Typography).attrs({
  fontSize: { _: "20px", tablet: "18px" },
  fontWeight: "normal",
  letterSpacing: { tablet: "1px" },
  mb: 1,
})``

const JumpLink = styled(Typography)`
  color: ${(props) => props.theme.colors.primary};
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 1px;
`
const CutParagraph = styled(Paragraph).attrs({
  fontSize: { _: "12px", tablet: "14px" },
  fontWeight: { _: "500", tablet: "300" },
  lineHeight: { _: "1.5", tablet: "18px" },
})``

const AnchorLink = styled(Box).attrs({
  position: "relative",
  top: "-150px",
  visability: "hidden",
})``

const Arrow = ({ next }) => (
  <ArrowImg
    alt={next ? "Next" : "Previous"}
    ratio="112 / 196"
    src={require("images/icons/arrow_left.svg")}
    width="15px"
  />
)

Arrow.propTypes = {
  next: PropTypes.bool,
}

const sliderSettings = {
  defaultControlsConfig: {
    nextButtonText: <Arrow next />,
    prevButtonText: <Arrow />,
  },
  dragging: false,
  speed: 500,
  swiping: false,
}

const SliderWrapper = styled(Slider)`
  .slider-control-centerright {
    margin-right: 0;
  }
  .slider-control-centerleft {
    margin-left: 0;
  }
  .slider-control-bottomcenter {
    margin-bottom: -39px;
  }
`
const DiamondCutSlider = () => {
  return (
    <Box width="90%">
      <SliderWrapper
        {...{
          frameAriaLabel: "Information about the cut of diamond",
          ...sliderSettings,
        }}
      >
        <Flex contain display={{ _: "block", tablet: "flex" }}>
          <Box width={{ tablet: 1 / 2 }}>
            <Video
              height="390px"
              margin="auto"
              poster={`${sl.config.jewlr_asset_url}videos/diamond/diamond_turning_colour.jpg`}
              video={`${sl.config.jewlr_asset_url}videos/diamond/turning_stone.mp4`}
              width="496px"
            />
          </Box>
          <Box alignSelf="center" textAlign="center" width={{ tablet: 1 / 2 }}>
            <Title color="white">CUT</Title>
            <Paragraph color="white" px={1}>
              The Cut is the most important of the 4Cs as it determines the
              brilliance of a diamond. Expertly cut diamonds sparkle the
              brightest because of the precise position, angle and shape of the
              facets, which allow light to reflect from the table (the top of
              the diamond).
            </Paragraph>
          </Box>
        </Flex>
        <Flex contain display={{ _: "block", tablet: "flex" }}>
          <Box width={{ tablet: 1 / 2 }}>
            <Image
              alt="TOO SHALLOW"
              mb={1}
              ratio="700 / 550"
              src={require("images/info/diamond/cuts_SHALLOW.svg")}
              width="496px"
            />
          </Box>
          <Box alignSelf="center" textAlign="center" width={{ tablet: 1 / 2 }}>
            <Title color="white">TOO SHALLOW</Title>
            <Paragraph color="white" px={1}>
              Shallow-cut diamonds can look larger in appearance, and are also
              known as &quot;spread diamonds.&quot; If a diamond is cut too
              shallow, the majority of light that enters is refracted from the
              bottom, resulting in a dull appearance.
            </Paragraph>
          </Box>
        </Flex>
        <Flex contain display={{ _: "block", tablet: "flex" }}>
          <Box width={{ tablet: 1 / 2 }}>
            <Image
              alt="TOO DEEP"
              mb={1}
              ratio="700 / 550"
              src={require("images/info/diamond/cuts_DEEP.svg")}
              width="496px"
            />
          </Box>
          <Box alignSelf="center" textAlign="center" width={{ tablet: 1 / 2 }}>
            <Title color="white">TOO DEEP</Title>
            <Paragraph color="white" px={1}>
              Diamonds that are cut too deep will refract more light than
              shallow-cut diamonds, but will still have less brilliance and
              sparkle than an excellent cut. Also, deep-cut diamonds tend to
              look smaller when put in a setting.
            </Paragraph>
          </Box>
        </Flex>
        <Flex contain display={{ _: "block", tablet: "flex" }}>
          <Box width={{ tablet: 1 / 2 }}>
            <Image
              alt="IDEAL CUT"
              mb={1}
              ratio="700 / 550"
              src={require("images/info/diamond/cuts_IDEAL.svg")}
              width="496px"
            />
          </Box>
          <Box alignSelf="center" textAlign="center" width={{ tablet: 1 / 2 }}>
            <Title color="white">IDEAL CUT</Title>
            <Paragraph color="white" px={1}>
              A well-cut diamond takes light in through each pavilion facet at
              an angle, which allows most of the light to reflect back through
              the crown (top). The light refracts upon exit, passing through the
              crown facets at a low angle.
            </Paragraph>
          </Box>
        </Flex>
      </SliderWrapper>
    </Box>
  )
}

const DiamondColorSlider = ({ color, onChange }) => {
  return (
    <SliderWrapper
      {...{
        frameAriaLabel: `Information about the ${jewlr_t(
          "terms.color"
        )} of diamond`,
        ...sliderSettings,
      }}
    >
      <div>
        <Flex display={{ _: "block", tablet: "flex" }}>
          <Box height="auto" width={{ tablet: 1 / 2 }}>
            <Video
              margin="auto"
              poster={`${sl.config.jewlr_asset_url}videos/diamond/turning_stone.jpg`}
              video={`${sl.config.jewlr_asset_url}videos/diamond/diamond_turning_colour.mp4`}
              width="100%"
            />
          </Box>
          <Box
            alignSelf="center"
            px={{ _: "5px", tablet: "50px" }}
            textAlign="center"
            width={{ tablet: 1 / 2 }}
          >
            <Title>COLOR</Title>
            <Paragraph px={3}>
              Color actually refers to a diamond&apos; s lack of color.
              Traditional diamond engagement rings are clear and valued based on
              colorlessness. The exceptions to this are &quot; fancy color&quot;
              diamonds, known for intense coloration in a range of shades such
              as pink and blue. Jewlr only carries diamonds of color H-E (near
              colorless to colorless) as valued by our Jewlr gemologists.
            </Paragraph>
          </Box>
        </Flex>
      </div>
      <div>
        <Flex
          display={{ _: "block", tablet: "flex" }}
          flexDirection="row"
          flexWrap="noWrap"
          pb={3}
        >
          <Box
            alignSelf="center"
            mb={2}
            textAlign="center"
            width={{ tablet: 1 / 2 }}
          >
            <Box mx="auto" width="80%">
              <Box position="relative">
                <Image
                  alt="Diamond Color"
                  opacity={color}
                  ratio="452 / 397"
                  src={require("images/product/diamond/diamond-color.svg")}
                  width="384px"
                />
                <Image
                  alt="Diamond Wireframe"
                  left="0"
                  position="absolute"
                  ratio="452 / 397"
                  src={require("images/product/diamond/diamond-wireframe.svg")}
                  top="0"
                  width="384px"
                />
              </Box>
              <Box>
                <DiamondImageSlide>
                  <DiamondInputSlider
                    max="1"
                    min="-0.3"
                    onChange={onChange}
                    step="0.1"
                    type="range"
                  />
                  <span style={{ float: "left", marginTop: "5px" }}>D</span>
                  <span style={{ float: "right", marginTop: "5px" }}>Z</span>
                </DiamondImageSlide>
              </Box>
            </Box>
          </Box>
          <Box alignSelf="center" textAlign="center" width={{ tablet: 1 / 2 }}>
            <Title>COLOR</Title>
            <Paragraph px={1}>
              <SubTitle>D-F (colorless)</SubTitle>- Color is virtually
              undetectable to the naked eye, visible only when compared
              side-by-side with higher grade stones.
            </Paragraph>
            <Paragraph lineHeight="1.5" px={1}>
              <SubTitle>G-H (near colorless)</SubTitle>- Color is difficult to
              detect, only visible against diamonds of a higher grade
            </Paragraph>
            <Paragraph lineHeight="1.5" px={1}>
              <SubTitle>I-J (near colorless)</SubTitle> - Containing slight
              traces of color, still virtually indetectable. Purchasing tip: I-J
              diamonds retail for about half the price of a D-F diamond, and
              about 20% less than a G-H. They are often deemed the best value
              diamonds.
            </Paragraph>
          </Box>
        </Flex>
        <Box margin="auto" width={{ _: "100%", tablet: "58%" }}>
          <Picture
            alt="Diamond Color"
            fallback={require("images/info/diamond/info_color-desktop.svg")}
            srcSet={[
              {
                height: "61",
                media: "(max-width: 767px)",
                sizes: "100%",
                srcSet: `${require("images/info/diamond/info_color-mobile.svg")} 767w`,
                width: "418",
              },

              {
                height: "62",
                media: "(min-width: 1024px)",
                sizes: "1920px",
                srcSet: `
              ${require("images/info/diamond/info_color-desktop.svg")} 1920w,
              `,
                width: "556",
              },
            ]}
          />
        </Box>
      </div>
    </SliderWrapper>
  )
}

DiamondColorSlider.propTypes = {
  color: PropTypes.string,
  onChange: PropTypes.func,
}

class Diamond extends React.Component {
  constructor(props) {
    super(props)
    this.state = { clarity: "0.1", colour: "0.4", weight: "0.70" } //default values
  }

  updateColour(e) {
    this.setState({ colour: e.target.value })
  }

  updateWeight(e) {
    var carratHash = ["0.50", "0.70", "1.00"]
    this.setState({ weight: carratHash[parseInt(e.target.value)] })
  }

  updateClarity(e) {
    this.setState({ clarity: e.target.value })
  }

  render() {
    return (
      <Box>
        <Box position="relative">
          <Picture
            alt="Diamonds &amp; The 4Cs"
            lazyload={false}
            mt={3}
            srcSet={[
              {
                height: 478,
                media: "(max-width: 767px)",
                sizes: "100vw",
                srcSet: `${require("images/info/diamond/diamond_mobile.jpg")} 767w,
               ${require("images/info/diamond/diamond_mobile_2x.jpg")} 1534w`,
                width: 767,
              },
              {
                height: 367,
                media: "(min-width: 768px) and (max-width: 1023px)",
                sizes: "100vw",
                srcSet: `${require("images/info/diamond/diamond_tablet.jpg")} 1005w,
               ${require("images/info/diamond/diamond_tablet_2x.jpg")} 2010w`,
                width: 1024,
              },
              {
                height: 367,
                media: "(min-width: 1024px)",
                sizes: "100vw",
                srcSet: `${require("images/info/diamond/diamond_desktop.jpg")} 1920w,
               ${require("images/info/diamond/diamond_desktop_2x.jpg")} 3840w`,
                width: 1920,
              },
            ]}
          />
          <Box
            height="100%"
            left={{ tablet: "58%" }}
            position="absolute"
            top={{ _: 3, desktop: "36%", tablet: "40%" }}
            width={{ _: "100%", desktop: "auto", tablet: "auto" }}
          >
            <Typography
              as="h1"
              fontSize={32}
              fontWeight="500"
              textAlign="center"
              width={{ tablet: "140px" }}
            >
              Diamonds &amp; The 4Cs
            </Typography>
          </Box>
        </Box>
        <Box contain>
          <Divider mb={4} />
          <Box
            bg="polar"
            mx={{ _: 2, tablet: "auto" }}
            px={{ _: 1, tablet: 3 }}
            py={{ _: 2, tablet: 3 }}
            textAlign="center"
            width={{ tablet: "75%" }}
          >
            <Title>THE FOREVER STONE</Title>
            <Paragraph mb={0} px={1}>
              Hypnotic brilliance. Unmatchable strength. Timeless beauty. The
              diamond is more than just a gemstone. It is an ancient symbol of
              resilience and endurance. It is a promise of unending devotion and
              everlasting love. It is a timeless emblem of elegance, grace and
              refinement. It is the Forever Stone for your forever love.
            </Paragraph>
          </Box>
          <Divider my={4} />
        </Box>
        <Flex contain display={{ _: "block", tablet: "flex" }}>
          <FloatImage>
            <Image
              alt="DIAMONDS & THE 4Cs"
              height={{ _: "150px", tablet: "142px" }}
              mb={1}
              src={require("images/info/diamond/DIAM_RD.png")}
              width={{ _: "150px", tablet: "142px" }}
            />
          </FloatImage>
          <Box px={2}>
            <Paragraph>
              Our mission here at Jewlr is to make sure that your diamond is
              perfect for you in every way. As part of the decision-making
              process, we want you to know as much as possible about the diamond
              you purchase and feel confident in the quality of your Jewlr
              piece. There are four factors to consider when determining the
              quality of a diamond, known in the industry as the 4Cs:{" "}
              <strong>C</strong>ut,
              <strong>C</strong>olour, <strong>C</strong>larity, and{" "}
              <strong>C</strong>arat Weight. Let the 4Cs be your guide as you
              create your Luxury Bridal Collection diamond engagement ring.
            </Paragraph>
            <Typography
              display={{ tablet: "none" }}
              fontSize="16px"
              fontWeight="300"
              mr="20px"
            >
              JUMP TO
            </Typography>
            <Flex gutter="4px" mb={2}>
              <Typography
                display={{ _: "none", tablet: "block" }}
                fontSize="14px"
                fontWeight="normal"
                letterSpacing="1px"
              >
                JUMP TO
              </Typography>
              <JumpLink>
                <a href="#cut-section">CUT</a>
              </JumpLink>
              <Typography fontSize="16px" fontWeight="300">
                /
              </Typography>
              <JumpLink>
                <a href="#carat-section">CARAT WEIGHT</a>
              </JumpLink>
              <Typography fontSize="16px" fontWeight="300">
                /
              </Typography>
              <JumpLink>
                <a href="#color-section">COLOR</a>
              </JumpLink>
              <Typography fontSize="16px" fontWeight="300">
                /
              </Typography>
              <JumpLink>
                <a href="#clarity-section">CLARITY</a>
              </JumpLink>
            </Flex>
          </Box>
        </Flex>
        <Flex bg="#000" justifyContent="center">
          <DiamondCutSlider />
        </Flex>
        <Box contain px={2}>
          <Flex contain display={{ _: "block", tablet: "flex" }} pt={3}>
            <Box
              height="auto"
              textAlign="-webkit-center"
              width={{ tablet: 1 / 2 }}
            >
              <Image
                alt="Diamond Cut"
                height={{ _: "446px", tablet: "352px" }}
                mb={1}
                src={require("images/product/diamond/diamond_cut.svg")}
                width={{ _: "660px", tablet: "480px" }}
              />
            </Box>
            <Box
              alignSelf="center"
              textAlign="center"
              width={{ tablet: 1 / 2 }}
            >
              <Picture
                alt="Diamond Cut"
                fallback={require("images/info/diamond/info_cut-desktop.svg")}
                srcSet={[
                  {
                    height: 105,
                    media: "(max-width: 767px)",
                    sizes: "570vw",
                    srcSet: `
                ${require("images/info/diamond/info_cut-mobile.svg")} 570w,
              `,
                    width: 570,
                  },
                  {
                    height: 70,
                    media: "(min-width: 768px)",
                    sizes: "439vw",
                    srcSet: `
                ${require("images/info/diamond/info_cut-desktop.svg")} 439w,
              `,
                    width: 439,
                  },
                ]}
              />
              <Paragraph
                fontSize={{ _: "12px", tablet: "16px" }}
                fontWeight={{ _: "500", tablet: "300" }}
                lineHeight={{ _: "1.5", tablet: "18px" }}
                mt={1}
                px={1}
              >
                Proportion, symmetry and polish make up the cut of a diamond.
                These factors affect a diamond’s appearance in three ways: fire
                (the rainbow-colored sparkle seen when a stone is exposed to
                light), brilliance (the colorless light that shines from the
                diamond), and scintillation (flashes or sparkle seen when a
                diamond is in motion). At Jewlr, we offer only diamonds ranked
                as <strong>Good</strong>,<strong>Very Good</strong>, or{" "}
                <strong>Excellent Cut</strong> to ensure the very best in
                quality and value.
              </Paragraph>
              <Flex contain display={{ _: "block", tablet: "flex" }}>
                <Box alignSelf="center" textAlign="center">
                  <CutSubTitle>GOOD CUT</CutSubTitle>
                  <CutParagraph>
                    A Good Cut reflects most of the light that enters through
                    the table, but less than that of a Very Good Cut. Good Cuts
                    represent about the top 25% of diamond cut quality.
                  </CutParagraph>
                </Box>
                <Box alignSelf="center" textAlign="center">
                  <CutSubTitle>VERY GOOD CUT</CutSubTitle>
                  <CutParagraph>
                    The Very Good Cut represents the top 15% of diamond cut
                    quality. It reflects near the same amount of light as an
                    ideal cut diamond, but at a lower price point.
                  </CutParagraph>
                </Box>
                <Box alignSelf="center" textAlign="center">
                  <CutSubTitle>EXCELLENT CUT</CutSubTitle>
                  <CutParagraph>
                    Excellent Cut diamonds are quite rare and reflect the most
                    light entering the stone. Representing the top 3% of diamond
                    cut quality, Excellent Cut may also be referred to as Ideal
                    Cut.
                  </CutParagraph>
                </Box>
              </Flex>
            </Box>
          </Flex>
          <Divider mb={3} />
          <AnchorLink id="cut-section" />
          <Box contain py={3} textAlign="-webkit-center">
            <Box
              alignSelf="center"
              textAlign="center"
              width={{ _: "auto", tablet: "85%" }}
            >
              <Paragraph
                fontSize={{ _: "18px", tablet: "16px" }}
                fontWeight="bold"
                letterSpacing={{ letterSpacing: "1px" }}
                mb={0}
              >
                CUT:
              </Paragraph>
              <Title>STONE SHAPE</Title>
              <Paragraph
                fontSize="18px"
                fontStyle="italic"
                fontWeight="500"
                lineHeight="1.5"
                mt={1}
                px={1}
              >
                The classic round brilliant cut remains the most popular, but
                diamonds are available in a variety of shapes. With Luxury
                Bridal Collection Signature setting, Jewlr is proud to offer a
                selection of styles that are timeless classics: round brilliant,
                oval, and princess. Look for more styles available soon.
              </Paragraph>
              <Flex contain display={{ _: "block", tablet: "flex" }}>
                <Box
                  alignSelf="center"
                  mb={2}
                  px={{ _: 3, tablet: 1 }}
                  textAlign="center"
                >
                  <Image
                    alt="Round Diamond"
                    height="60px"
                    mb={1}
                    src={`${sl.config.jewlr_asset_url}images/product/diamond/rd/1.00ct.svg`}
                    width="60px"
                  />
                  <CutSubTitle>ROUND</CutSubTitle>
                  <Paragraph
                    fontSize="12px"
                    fontWeight="500"
                    lineHeight="1.5"
                    mb={0}
                  >
                    The quintessential engagement ring shape with a perfectly
                    circular top, a round brilliant diamond is the symbol of a
                    love that never ends.
                  </Paragraph>
                  <Typography
                    as={Link}
                    color="primary"
                    data-lc="diamond"
                    letterSpacing="1px"
                    to="/the-one-and-only?filtered_tags=round-cut"
                  >
                    SHOP ROUND
                  </Typography>
                </Box>
                <Box
                  alignSelf="center"
                  mb={2}
                  px={{ _: 3, tablet: 1 }}
                  textAlign="center"
                >
                  <Image
                    alt="Oval Diamond"
                    height="60px"
                    mb={1}
                    src={`${sl.config.jewlr_asset_url}images/product/diamond/ov/1.00ct.svg`}
                    width="60px"
                  />
                  <CutSubTitle>OVAL</CutSubTitle>
                  <Paragraph
                    fontSize="12px"
                    fontWeight="500"
                    lineHeight="1.5"
                    mb={0}
                  >
                    Oval diamonds are a nod to tradition with a splash of
                    individuality. An oval diamond can be cut to the same
                    brilliance as a round stone, making it just as elegant and
                    unique as its wearer.{" "}
                  </Paragraph>
                  <Typography
                    as={Link}
                    color="primary"
                    data-lc="diamond"
                    letterSpacing="1px"
                    to="/the-one-and-only?filtered_tags=oval-cut"
                  >
                    SHOP OVAL
                  </Typography>
                </Box>
                <Box
                  alignSelf="center"
                  mb={2}
                  px={{ _: 3, tablet: 1 }}
                  textAlign="center"
                >
                  <Image
                    alt="Princess Diamond"
                    height="60px"
                    mb={1}
                    src={`${sl.config.jewlr_asset_url}images/product/diamond/pc/1.00ct.svg`}
                    width="60px"
                  />
                  <CutSubTitle>PRINCESS</CutSubTitle>
                  <Paragraph
                    fontSize="12px"
                    fontWeight="500"
                    lineHeight="1.5"
                    mb={0}
                  >
                    With its unmistakable sparkle, a princess cut diamond
                    demands attention. Prepare to catch eyes, turn heads, and
                    feel like royalty.
                  </Paragraph>
                  <Typography
                    as={Link}
                    color="primary"
                    data-lc="diamond"
                    letterSpacing="1px"
                    to="/the-one-and-only?filtered_tags=princess-cut"
                  >
                    SHOP PRINCESS
                  </Typography>
                </Box>
              </Flex>
            </Box>
          </Box>
          <Divider color="primary" mb={3} />
          <AnchorLink id="carat-section" />
          <Flex contain pt={3}>
            <Box textAlign="-webkit-center" width={{ tablet: 1 / 2 }}>
              <Picture
                alt="Diamond Clarity"
                fallback={require("images/info/diamond/carat.jpg")}
                srcSet={[
                  {
                    height: 103,
                    media: "(max-width: 767px)",
                    sizes: "100vw",
                    srcSet: `
                    ${require("images/info/diamond/carat_mobile.jpg")} 767w,
                  `,
                    width: 229,
                  },
                  {
                    height: 219,
                    media: "(min-width: 768px)",
                    sizes: "100vw",
                    srcSet: `
                    ${require("images/info/diamond/carat.jpg")} 334w,
                    ${require("images/info/diamond/carat_2x.jpg")} 688w
                  `,
                    width: 334,
                  },
                ]}
                width={{ _: "229px", tablet: "334px" }}
              />
              <Title>CARAT WEIGHT</Title>
              <Paragraph
                fontSize={{ _: "12px", tablet: "16px" }}
                fontWeight={{ _: "500", tablet: "300" }}
                lineHeight={{ _: "1.5", tablet: "18px" }}
                mt={1}
                px={1}
              >
                Carat is the measurement of a diamond by weight. The standard is
                1.00 carat = .20 grams. Certain factors can make a stone look
                larger than its carat weight such as the size of the crown and
                the shape. Round, oval and marquise cut diamonds will appear
                larger to the eye, while princess cuts may look smaller. Keep in
                mind, carat alone does not determine the value of a diamond.
              </Paragraph>
              <DiamondImageSlide>
                <DiamondInputSlider
                  max="2"
                  min="0"
                  onChange={this.updateWeight.bind(this)}
                  step="1"
                  type="range"
                />
              </DiamondImageSlide>
              <Box mb={3} textAlign="center" width="75%">
                <Flex>
                  <Box width="32%">
                    <p>0.50 ct</p>
                    <Image
                      alt="Carat Weight"
                      height="36px"
                      src={`${sl.config.jewlr_asset_url}images/product/diamond/rd/0.50ct.svg`}
                      width="36px"
                    />
                  </Box>
                  <Box width="32%">
                    <p>0.70 ct</p>
                    <Image
                      alt="Carat Weight"
                      height="36px"
                      src={`${sl.config.jewlr_asset_url}images/product/diamond/rd/0.70ct.svg`}
                      width="36px"
                    />
                  </Box>
                  <Box width="32%">
                    <p>1.00 ct</p>
                    <Image
                      alt="Carat Weight"
                      height="36px"
                      src={`${sl.config.jewlr_asset_url}images/product/diamond/rd/1.00ct.svg`}
                      width="36px"
                    />
                  </Box>
                </Flex>
              </Box>
            </Box>
            <Box
              alignSelf="center"
              position="relative"
              textAlign="center"
              width={1 / 2}
            >
              <MediaBox greaterThanOrEqual="tablet">
                <Image
                  alt="Hand"
                  bottom="-200px"
                  height={{ desktop: "463px", tablet: "324px" }}
                  left="0px"
                  position="absolute"
                  src={require("images/info/diamond/infopages_hand.svg")}
                  width={{ desktop: "480px", tablet: "336px" }}
                />
                <Image
                  alt="Diamond"
                  bottom="60px"
                  height="25px"
                  position="absolute"
                  right="235px"
                  src={`${sl.config.jewlr_asset_url}images/product/diamond/rd/${this.state.weight}ct.svg`}
                  transform="scale"
                  width="25px"
                />
              </MediaBox>
            </Box>
          </Flex>
          <Divider color="primary" mb={3} mt={0} />
          <AnchorLink id="color-section" />
          <Box contain pt={3}>
            <DiamondColorSlider
              color={this.state.colour}
              onChange={this.updateColour.bind(this)}
            />
          </Box>
          <Box height="30px" />
          <Divider color="primary" mb={3} />
          <AnchorLink id="clarity-section" />
          <Box contain>
            <Flex display={{ _: "block", tablet: "flex" }} pb={3}>
              <Box
                alignSelf="center"
                textAlign="center"
                width={{ tablet: 1 / 2 }}
              >
                <Title>CLARITY</Title>
                <Paragraph
                  fontSize={{ _: "12px", tablet: "16px" }}
                  fontWeight={{ _: "500", tablet: "300" }}
                  lineHeight={{ _: "1.5", tablet: "18px" }}
                  px={{ _: "5px", tablet: "84px" }}
                >
                  Clarity grades diamonds based on internal flaws and external
                  imperfections. Known as inclusions these flaws refer to
                  clouding and pinpoints within the stone, as well as scratches
                  or nicks on the surface. Clarity is graded on a lettered and
                  numbered scale, ranging from I3 (Included) to FL (Flawless).
                </Paragraph>
              </Box>
              <Box
                alignSelf="center"
                position="relative"
                px={{ _: "5px", tablet: "50px" }}
                textAlign="center"
                width={{ tablet: 1 / 2 }}
              >
                <Flex flexDirection="column" flexWrap="noWrap">
                  <Box position="relative" width="100%">
                    <Picture
                      alt="Diamond Clarity"
                      fallback={require("images/product/diamond/diamond_flawless_2x.png")}
                      srcSet={[
                        {
                          height: 600,
                          media: "(max-width: 767px)",
                          sizes: "600vw",
                          srcSet: `
                ${require("images/product/diamond/diamond_flawless_mobile.png")} 600w,
              `,
                          width: 600,
                        },
                        {
                          height: 175,
                          media: "(min-width: 768px)",
                          sizes: "175vw",
                          srcSet: `
                ${require("images/product/diamond/diamond_flawless.png")} 175w,
                ${require("images/product/diamond/diamond_flawless_2x.png")} 350w
              `,
                          width: 175,
                        },
                      ]}
                    />
                    <Box
                      opacity={this.state.clarity}
                      position="absolute"
                      top="0"
                      width="100%"
                    >
                      <Picture
                        alt="Diamond Clarity"
                        fallback={require("images/product/diamond/diamond_flawed_2x.png")}
                        mb={1}
                        srcSet={[
                          {
                            height: 600,
                            media: "(max-width: 767px)",
                            sizes: "600vw",
                            srcSet: `
                ${require("images/product/diamond/diamond_flawed_mobile.png")} 600w,
              `,
                            width: 600,
                          },
                          {
                            height: 175,
                            media: "(min-width: 768px)",
                            sizes: "175vw",
                            srcSet: `
                ${require("images/product/diamond/diamond_flawed.png")} 175w,
                ${require("images/product/diamond/diamond_flawed_2x.png")} 350w
              `,
                            width: 175,
                          },
                        ]}
                      />
                    </Box>
                  </Box>

                  <Box width="100%">
                    <DiamondImageSlide>
                      <DiamondInputSlider
                        max="0.5"
                        min="-0.3"
                        onChange={this.updateClarity.bind(this)}
                        step="0.1"
                        type="range"
                      />
                      <div
                        style={{
                          lineHeight: "1",
                          margin: "3px -13%",
                          textAlign: "center",
                          width: "120%",
                        }}
                      >
                        <LabelLeft>Flawless (FL)</LabelLeft>
                        <LabelRight>
                          Visible
                          <br />
                          Inclusion (I3)
                        </LabelRight>
                      </div>
                    </DiamondImageSlide>
                  </Box>
                </Flex>
              </Box>
            </Flex>
            <Box textAlign="center">
              <Picture
                alt="Diamond Clarity"
                fallback={require("images/info/diamond/info_clarity-desktop.svg")}
                mb={1}
                srcSet={[
                  {
                    height: 105,
                    media: "(max-width: 767px)",
                    sizes: "649vw",
                    srcSet: `
                ${require("images/info/diamond/info_clarity-mobile.svg")} 649w,
              `,
                    width: 649,
                  },
                  {
                    height: 70,
                    media: "(min-width: 768px)",
                    sizes: "529vw",
                    srcSet: `
                ${require("images/info/diamond/info_clarity-desktop.svg")} 529w,
              `,
                    width: 529,
                  },
                ]}
                width={{ _: "649px", tablet: "529px" }}
              />
            </Box>
          </Box>
          <Divider color="primary" mb={3} />
          <Box contain>
            <Flex display={{ _: "block", tablet: "flex" }}>
              <Box textAlign="center" width={{ tablet: 1 / 3 }}>
                <Image
                  alt="The One"
                  height="200px"
                  src={require("images/info/diamond/theone.svg")}
                  width="200px"
                />
              </Box>
              <Box textAlign="center" width={{ tablet: 2 / 3 }}>
                <Typography
                  as="h2"
                  fontSize="18px"
                  fontWeight={{ tablet: "normal" }}
                  letterSpacing={{ tablet: "1px" }}
                >
                  DIAMOND CURATION
                </Typography>
                <Paragraph fontSize="16px" mt={1}>
                  Finding the perfect diamond is a journey, and we want to help
                  make it a joyous one. Whether you&apos;re looking for a
                  special shape, a certain grade of clarity or that special
                  sparkle, let our Jewlr diamond experts guide you. As you build
                  your Luxury Bridal Collection diamond engagement ring, look
                  for Jewlr&apos;s THE ONE diamond seal. This is both a purchase
                  suggestion and a promise: that this diamond is not only
                  &quot;the one&quot; for your one and only, but also the best
                  quality and value we can offer.
                </Paragraph>
                <Typography
                  as={Link}
                  data-lc="diamond"
                  to="/info/diamond_curation"
                >
                  <Image
                    alt="The One Diamond Seal"
                    height="29px"
                    src={require("images/info/diamond/jewlr_theone_callout.svg")}
                    width="235px"
                  />
                  <SectionTitle fontWeight="500" py={2} textAlign="center">
                    LEARN MORE
                  </SectionTitle>
                </Typography>
              </Box>
            </Flex>
          </Box>
          <Divider color="primary" mb={3} />
          <Box contain>
            <Flex display={{ _: "block", tablet: "flex" }} textAlign="center">
              <Box width={{ tablet: 1 / 2 }}>
                <Typography
                  as="h2"
                  fontSize="18px"
                  fontWeight={{ tablet: "normal" }}
                  letterSpacing={{ tablet: "1px" }}
                >
                  DIAMOND ETHICS
                </Typography>
                <Paragraph fontWeight={{ _: "500", tablet: "300" }} px={1}>
                  At Jewlr, we believe the most beautiful diamonds are those
                  that are responsibly sourced. We understand that our customers
                  care about the origins of their diamonds, and therefore have
                  committed to maintaining the highest of standards when it
                  comes to the diamonds we sell. Proudly North American, Jewlr
                  fully supports local and international initiatives adhering to
                  the{" "}
                  <Typography
                    as="a"
                    color="primary"
                    fontSize="16px"
                    href="https://www.kimberleyprocess.com/"
                    rel="noopener"
                    target="_blank"
                  >
                    Kimberley Process Certification Scheme (KPCS)
                  </Typography>
                  , and various United Nations agreements in the effort to
                  eradicate “blood diamonds” and uphold ethics within the
                  diamond trade.{" "}
                </Paragraph>
                <Paragraph fontWeight={{ _: "500", tablet: "300" }} px={1}>
                  Our diamonds are conflict-free, and obtained through reputable
                  suppliers in the industry who also adhere to the KPCS and
                  similar efforts. Legitimate trading of diamonds can be a
                  source of positive change in many developing nations.
                </Paragraph>
                <Typography
                  color="primary"
                  fontSize={{ _: "18px", tablet: "16px" }}
                  fontWeight="500"
                >
                  Be confident that our diamonds contribute to a more ethical
                  industry and a brighter future for many.
                </Typography>
              </Box>
              <Divider color="primary" my={3} />
              <Box width={{ tablet: 1 / 2 }}>
                <Typography
                  as="h2"
                  fontSize="18px"
                  fontWeight={{ tablet: "normal" }}
                  letterSpacing={{ tablet: "1px" }}
                >
                  DIAMOND CERTIFICATION
                </Typography>
                <Paragraph fontWeight={{ _: "500", tablet: "300" }} px={1}>
                  Your One & Only engagement ring will include a certificate of
                  quality assurance from either the Gemological Institute of
                  America (GIA) or the International Gemological Institute (IGI)
                  for your diamond, and will be sent with your ring. As the most
                  prestigious international association of its kind, the IGI
                  Laboratory Report is the language of trust amongst reputable
                  jewelers and jewelry suppliers everywhere. GIA&apos;s
                  establishment of the 4Cs and the International Diamond Grading
                  System has set the standard for determining diamond quality
                  worldwide. Know that when you receive a GIA/IGI report with
                  your diamond purchase, you hold in your hand a guarantee that
                  your diamond is the very best available on the market.
                </Paragraph>
                <Flex
                  justifyContent="center"
                  margin="auto"
                  width={{ tablet: "55%" }}
                >
                  <Picture
                    alt="GIA"
                    fallback={require("images/product/diamond/GIA-Logo.jpg")}
                    height="67px"
                    mb={1}
                    srcSet={[
                      {
                        media: "(min-width: 768px)",
                        sizes: "150vw",
                        srcSet: `
                 ${require("images/product/diamond/GIA-Logo.jpg")} 150w,
                ${require("images/product/diamond/GIA-Logo_2x.jpg")} 300w,
              `,
                      },
                    ]}
                    width="150px"
                  />

                  <Image
                    alt="GIA"
                    height="105px"
                    mb={1}
                    src={require("images/product/diamond/igi.svg")}
                    width="105px"
                  />
                </Flex>
                <p>
                  Your diamond is guaranteed to be certified by one of these two
                  prestigious gemological laboratories.
                </p>
              </Box>
            </Flex>
          </Box>
          <Divider color="primary" mb={3} />
          <Flex contain display={{ _: "block", tablet: "flex" }} mb={4}>
            <Box textAlign="center" width={{ tablet: 1 / 2 }}>
              <Picture
                alt="Signature Collection"
                fallback={require("images/info/diamond/shop_collection_2x.jpg")}
                mb={1}
                srcSet={[
                  {
                    height: 493,
                    media: "(max-width: 767px)",
                    sizes: "767vw",
                    srcSet: `
                ${require("images/info/diamond/shop_collection_mobile.jpg")} 767w,
              `,
                    width: 767,
                  },
                  {
                    height: 360,
                    media: "(min-width: 768px)",
                    sizes: "560vw",
                    srcSet: `
                ${require("images/info/diamond/shop_collection.jpg")} 560w,
                ${require("images/info/diamond/shop_collection_2x.jpg")} 1119w
              `,
                    width: 560,
                  },
                ]}
              />
            </Box>
            <Box
              mt={{ desktop: 2 }}
              textAlign="center"
              width={{ tablet: 1 / 2 }}
            >
              <Image
                alt="GIA"
                height="112px"
                mb={{ _: 3, tablet: 4 }}
                src={require("images/info/diamond/luxury-bridal-collection.svg")}
                width="336px"
              />

              <Paragraph fontSize="18px" fontWeight="500">
                Ready for the next step?
                <br />
                Create your Luxury Bridal Collection diamond engagement ring
                today.
              </Paragraph>
              <ShopButton data-lc="diamond" to="/the-luxury-collection">
                Shop Now
              </ShopButton>
            </Box>
          </Flex>
        </Box>
      </Box>
    )
  }
}

export default Diamond
